import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Check, Eye, EyeOff } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { GoogleIcon } from '../components/GoogleIcon'
import { Logo } from '../components/Logo'
import { Button, Divider, Input, Text } from '../components/ui'
import { signup } from '../lib/api'
import { authClient } from '../lib/auth-client'
import { getPlatformKbDomain } from '../lib/plan-limits'
import { generateSlug } from '../lib/slugify'
import { cn } from '../lib/cn'

const signupSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Invalid email address'),
  workspaceName: z.string().min(2, 'Workspace name must be at least 2 characters'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
})

type SignupForm = z.infer<typeof signupSchema>

export const Route = createFileRoute('/signup')({
  component: SignupPage,
})

function SignupPage() {
  const navigate = useNavigate()
  const [showPassword, setShowPassword] = useState(false)
  const [globalError, setGlobalError] = useState<string | null>(null)
  const [googleLoading, setGoogleLoading] = useState(false)

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<SignupForm>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      workspaceName: '',
      password: '',
    },
  })

  const workspaceName = watch('workspaceName')
  const slug = generateSlug(workspaceName)

  async function onSubmit(data: SignupForm) {
    setGlobalError(null)
    const result = await signup(data)

    if (!result.success) {
      if (result.error.code === 'SLUG_TAKEN') {
        setError('workspaceName', { message: result.error.message })
        return
      }
      setGlobalError(result.error.message)
      return
    }

    navigate({ to: '/verify-email', search: { email: data.email } })
  }

  async function handleGoogleSignup() {
    setGlobalError(null)
    setGoogleLoading(true)
    try {
      await authClient.signIn.social({
        provider: 'google',
        callbackURL: '/',
      })
    } catch {
      setGlobalError('Could not sign in with Google. Please try again.')
      setGoogleLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-6 py-12">
      <div className="w-full max-w-[480px]">
        <div className="mb-8 flex flex-col items-center text-center">
          <Logo className="mb-6" />
          <Text variant="h1" className="mb-2">
            Get started with SonderSoft
          </Text>
          <Text variant="body-sm" className="text-foreground/60">
            Enter your account information to get started.
          </Text>
        </div>

        <div className="space-y-4">
          <Button
            variant="outline"
            fullWidth
            size="lg"
            leftIcon={<GoogleIcon />}
            loading={googleLoading}
            onClick={handleGoogleSignup}
          >
            Continue with Google
          </Button>

          <Divider />

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="First name"
                placeholder="Jane"
                error={errors.firstName?.message}
                {...register('firstName')}
              />
              <Input
                label="Last name"
                placeholder="Doe"
                error={errors.lastName?.message}
                {...register('lastName')}
              />
            </div>

            <Input
              label="Email"
              type="email"
              placeholder="you@company.com"
              autoComplete="email"
              error={errors.email?.message}
              {...register('email')}
            />

            <Input
              label="Workspace name"
              placeholder="Acme Inc"
              error={errors.workspaceName?.message}
              {...register('workspaceName')}
            />

            {slug && (
              <div className="flex items-start gap-2 rounded-2xl bg-stone px-4 py-3">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-foreground/60" />
                <Text variant="body-sm" className="text-foreground/70">
                  Your site will be at{' '}
                  <span className="font-semibold text-foreground">
                    {slug}.{getPlatformKbDomain()}
                  </span>
                </Text>
              </div>
            )}

            <Input
              label="Password"
              type={showPassword ? 'text' : 'password'}
              placeholder="At least 8 characters"
              autoComplete="new-password"
              error={errors.password?.message}
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="text-foreground/50 hover:text-foreground"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              }
              {...register('password')}
            />

            {globalError && (
              <Text variant="body-sm" className="text-red-600">
                {globalError}
              </Text>
            )}

            <Button type="submit" fullWidth size="lg" loading={isSubmitting}>
              Continue
            </Button>
          </form>
        </div>

        <Text variant="caption" className={cn('mt-6 block text-center')}>
          By signing up, you agree to the Terms of Service and Privacy Policy.
        </Text>

        <Text variant="body-sm" className="mt-4 text-center text-foreground/60">
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-foreground underline">
            Log in
          </Link>
        </Text>
      </div>
    </div>
  )
}
